/**
 * GalleryPart — renders a filtered slice of the embedding page's scope's
 * Gallery.
 *
 * Calls the public, page-scoped items endpoint when pageId/partId are
 * supplied (anonymous-friendly; backend validates Page.isPublic before
 * returning anything). The PageEditor preview pane renders this without
 * those props — there we keep a descriptive placeholder.
 */

import { useEffect, useState } from 'react';
import { ApiClientError, pagesApi, type GalleryItem, type GalleryPartConfig } from '@/services/api';

interface GalleryPartProps {
  config: GalleryPartConfig;
  pageId?: string;
  partId?: string;
}

export function GalleryPart({ config, pageId, partId }: GalleryPartProps) {
  const [items, setItems] = useState<GalleryItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasRoute = pageId && partId;

  useEffect(() => {
    if (!hasRoute) {
      setItems(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    pagesApi
      .listPublicPartItems(pageId, partId)
      .then((res) => {
        if (!cancelled) setItems(res.items);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // 404 = page not public / part not a gallery part — degrade
        // quietly with a friendly empty state instead of an error.
        if (err instanceof ApiClientError && err.status === 404) {
          setItems([]);
        } else {
          setError(err instanceof Error ? err.message : 'Failed to load gallery');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [hasRoute, pageId, partId]);

  const summary = describeFilter(config.filter);

  if (!hasRoute) {
    return (
      <section className="max-w-5xl mx-auto px-4 py-6">
        <div className="rounded-lg border border-dashed border-white/20 bg-white/5 p-6 text-center">
          <p className="text-white/60 text-sm uppercase tracking-wide">Gallery</p>
          <p className="text-white/80 mt-2">{summary}</p>
          <p className="text-xs text-white/40 mt-3">
            Live items render once this page is saved and viewed at its public URL.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="max-w-5xl mx-auto px-4 py-6">
      <div className="mb-3">
        <p className="text-xs uppercase tracking-wide text-white/40">Gallery</p>
        <p className="text-white/60 text-sm">{summary}</p>
      </div>
      {loading && <p className="text-white/60 text-sm">Loading photos…</p>}
      {error && <p className="text-red-400 text-sm">{error}</p>}
      {!loading && !error && items && items.length === 0 && (
        <p className="text-white/60 text-sm">No photos match this filter yet.</p>
      )}
      {items && items.length > 0 && (
        <div
          className={
            config.layout === 'masonry'
              ? 'columns-2 md:columns-3 lg:columns-4 gap-3 [column-fill:_balance]'
              : 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3'
          }
        >
          {items.slice(0, config.maxItems ?? 24).map((it) => (
            <figure
              key={it.id}
              className={
                config.layout === 'masonry'
                  ? 'mb-3 break-inside-avoid bg-black/30 rounded overflow-hidden'
                  : 'bg-black/30 rounded overflow-hidden'
              }
            >
              {it.mediaKind === 'video' ? (
                <video
                  src={it.url ?? it.blobUrl}
                  poster={it.posterBlobUrl ?? undefined}
                  controls
                  className="w-full"
                />
              ) : (
                <img
                  src={it.thumbnailUrl ?? it.blobUrl}
                  alt={it.caption ?? ''}
                  className={
                    config.layout === 'masonry'
                      ? 'w-full h-auto block'
                      : 'w-full aspect-square object-cover'
                  }
                  loading="lazy"
                />
              )}
              {it.caption && (
                <figcaption className="px-2 py-1.5 text-xs text-white/70">{it.caption}</figcaption>
              )}
            </figure>
          ))}
        </div>
      )}
    </section>
  );
}

function describeFilter(filter: GalleryPartConfig['filter']): string {
  const bits: string[] = [];
  if (filter.folderPath) bits.push(`folder “${filter.folderPath}”`);
  else if (filter.folderId) bits.push('a specific folder');
  if (filter.tags && filter.tags.length > 0) bits.push(`tags: ${filter.tags.join(', ')}`);
  if (filter.mediaKind) bits.push(filter.mediaKind);
  return bits.length === 0 ? 'All photos in this gallery' : bits.join(' · ');
}

export default GalleryPart;
